import {
  type DonationModelPagedResult,
  type GetApiDonationsByDateQueryParams,
  getApiDonationsByDate,
} from '@/shared/api'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

export interface DonationsByDateQueryModelBase {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Filters
  $filters: StoreWritable<GetApiDonationsByDateQueryParams>
  filtersInit: EventCallable<Partial<GetApiDonationsByDateQueryParams>>
  filtersChanged: EventCallable<Partial<GetApiDonationsByDateQueryParams>>
  filtersReset: EventCallable<void>
  //   Search
  $search: StoreWritable<string | null>
  searchChanged: EventCallable<string>
  searchReset: EventCallable<void>
  //   Sort
  $sort: StoreWritable<DonationsByDateSortState>
  sortChanged: EventCallable<DonationsByDateSortState>
  sortReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: GetApiDonationsByDateQueryParams
  }>
  // Query
  query: Query<
    {
      params?: GetApiDonationsByDateQueryParams
    },
    DonationModelPagedResult,
    unknown
  >
  refreshed: EventCallable<void>
}

export interface DonationsByDateQueryModelWithPagination
  extends DonationsByDateQueryModelBase {
  $page: StoreWritable<number>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

interface DonationsByDateQueryModelParams {
  initialFilters?: Partial<GetApiDonationsByDateQueryParams>
  usePagination: boolean
  accumulate: boolean
}

export interface DonationsByDateSortState {
  field: 'nickname' | 'amount' | 'date' | null
  order: 'asc' | 'desc' | null
}

export function createDonationsByDateQueryModel(
  {
    initialFilters = {},
    usePagination = true,
  }: DonationsByDateQueryModelParams = {
    initialFilters: {},
    usePagination: true,
    accumulate: false,
  },
): DonationsByDateQueryModelWithPagination | DonationsByDateQueryModelBase {
  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Filters
  const filtersInit = createEvent<Partial<GetApiDonationsByDateQueryParams>>()
  const filtersChanged =
    createEvent<Partial<GetApiDonationsByDateQueryParams>>()
  const filtersReset = createEvent()
  const $filters = createStore<GetApiDonationsByDateQueryParams>(initialFilters)
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .on(filtersReset, () => initialFilters)

  // Search
  const searchChanged = createEvent<string>()
  const searchReset = createEvent()
  const $search = createStore<string | null>(null)

  //   Sort
  const initialSort: DonationsByDateSortState = { field: null, order: null }
  const sortChanged = createEvent<DonationsByDateSortState>()
  const sortReset = createEvent()

  const $sort = createStore<DonationsByDateSortState>(initialSort)
    .on(sortChanged, (_, payload) => payload)
    .on(sortReset, () => initialSort)

  // Pagination

  const $page = createStore<number>(DEFAULT_PAGE)

  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .on(pageReset, () => DEFAULT_PAGE)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  sample({
    clock: [$filters, $search, $sort],
    target: pageReset,
  })

  // Query params
  const $params = combine(
    $filters,
    $search,
    $page,
    $pageSize,
    $sort,
    (filters, search, page, pageSize, sort) => {
      // Filters
      const params: GetApiDonationsByDateQueryParams = { ...filters }
      if (search) params.SearchQuery = search.trim()
      // Sort
      if (sort.field && sort.order) {
        if (sort.field === 'nickname') {
          if (sort.order === 'asc') params.SortByName = true
          else params.SortByNameDesc = true
        } else if (sort.field === 'amount') {
          if (sort.order === 'asc') params.SortByAmount = true
          else params.SortByAmountDesc = true
        } else if (sort.field === 'date') {
          if (sort.order === 'asc') params.SortByDate = true
          else params.SortByDateDesc = true
        }
      }
      // Pagination
      if (usePagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { params }
    },
  )

  // Query
  const query = createQuery({ handler: getApiDonationsByDate })

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  const searchChangedDebounced = debounce({
    source: searchChanged,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  const searchResetDebounced = debounce({
    source: searchReset,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Change search

  $search
    .on(searchChangedDebounced, (_, search) => search)
    .reset(searchResetDebounced)

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [filtersReset, searchReset, sortReset, pageReset],
  })

  const model: DonationsByDateQueryModelBase = {
    // Availability
    $isAvailable,
    availableSet,
    unavailableSet,
    // Filters
    $filters,
    filtersInit,
    filtersChanged,
    filtersReset,
    // Search
    $search,
    searchChanged,
    searchReset,
    // Sort
    $sort,
    sortChanged,
    sortReset,
    // Params
    $params,
    // Query
    query,
    refreshed,
  }

  const modelWithPagination: DonationsByDateQueryModelWithPagination = {
    ...model,
    $page,
    pageChanged,
    pageReset,
    $pageSize,
    pageSizeChanged,
  }

  if (usePagination) {
    return modelWithPagination
  }

  return model
}
